      
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import zh from './locales/zh.json';
import en from './locales/en.json';
import BoosterNativeBridge from './jsBridge';

const LANG_STORAGE_KEY = 'locale';
const DEFAULT_LANG = 'zh';

// 从 URL 读取 App 打开 H5 时传入的语言参数：?lang=zh / ?lang=en
const getLangFromUrl = () => {
  const lang = new URLSearchParams(window.location.search).get('lang');
  return lang === 'en' || lang === 'zh' ? lang : null;
};

// 同步获取语言（初始化 i18n 用，无法等异步 Native）：URL lang > 本地缓存 > 默认
export const getLanguage = () =>
  getLangFromUrl() ||
  localStorage.getItem(LANG_STORAGE_KEY) ||
  DEFAULT_LANG;

// 写入语言变量并切换 i18n 语言
// notifyNative：H5 内主动切换（登录页语言 Tab / window.setLanguage）时回传 App，让 App 也跟着切；
// 由 Native 推送、URL 参数发起的同步传 false，避免把同一个语言再回传一遍（回声）
export const setLanguage = (lang, { notifyNative = true } = {}) => {
 const normalized = (lang === 'en' || lang === 'zh') ? lang : i18n.language;
  localStorage.setItem(LANG_STORAGE_KEY, normalized);
  document.documentElement.setAttribute('lang', normalized);
  if (notifyNative) {
    // 非 Native 环境或 App 未实现 setLocale 命令时静默跳过，不影响 H5 自身切语言
    try {
      BoosterNativeBridge.setLocale?.(normalized);
    } catch (e) {
      console.warn('[i18n] 回传 Native 语言失败:', e);
    }
  }
  return i18n.changeLanguage(normalized);
};

// 挂到 window，便于控制台调试切换语言：setLanguage('en')
window.setLanguage = setLanguage;

// 向 Native 询问并同步语言；拿不到有效语言时返回 null，由调用方兜底
export const syncFromNative = async () => {
  try {
    const { locale } = await BoosterNativeBridge.getLocale();
    console.log('[i18n] syncFromNative → locale=', locale);
    if (locale === 'en' || locale === 'zh') {
      await setLanguage(locale, { notifyNative: false });
      return locale;
    }
  } catch (e) {
    console.warn('[i18n] syncFromNative failed:', e);
  }
  return null;
};

// 初始化语言：URL lang > Native > 本地缓存 > 默认
export const initLocale = async () => {
  const urlLang = getLangFromUrl();
  console.log('[i18n] initLocale → urlLang=', urlLang, 'localStorage=', localStorage.getItem(LANG_STORAGE_KEY));
  // 1. App 打开 H5 传入的 lang 优先
  if (urlLang) {
    await setLanguage(urlLang, { notifyNative: false });
    return urlLang;
  }
  // 2. 向 Native 询问（主来源）
  const nativeLang = await syncFromNative();
  if (nativeLang) return nativeLang;
  // 3. 本地缓存兜底
  const cached = localStorage.getItem(LANG_STORAGE_KEY);
  if (cached) {
    await setLanguage(cached, { notifyNative: false });
    return cached;
  }
  // 4. 默认语言
  return DEFAULT_LANG;
};

// 接收 Native 主动推送的语言变化（App 切语言后、WebView 已加载时执行）
// 契约：Native 调用 window.onLocaleChanged('zh' | 'en')，H5 只负责切换语言
export const initLocaleListener = () => {
   console.log('[i18n] initLocaleListener 执行了');
  BoosterNativeBridge.setLocaleChangeListener((lang) => {
       console.log('[i18n] 监听器触发, lang:', lang);
    // 来源是 Native，不再回传，避免回声
    setLanguage(lang, { notifyNative: false });
  });
};


i18n.use(initReactI18next).init({
  resources: {
    zh: { translation: zh },
    en: { translation: en },
  },
  lng: getLanguage(),
  fallbackLng: DEFAULT_LANG,
  interpolation: {
    // React 已做 XSS 转义，关闭 i18next 的转义
    escapeValue: false,
  },
});

// 同步 <html lang>，供 CSS 用 html[lang="en"] 做英文版差异化（如整体字号调小）
document.documentElement.setAttribute('lang', getLanguage());

export default i18n;

    